import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { isNil } from "lodash";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";

import { AnswerFeedbackScore, ChatMessage } from "../../types";
import { useApiClient } from "../hooks/useApiClient";
import { Components, Paths, PathsDictionary } from "../schema/openapi";
import { MetadataQueryKey } from "../settings/settings.api";

type AskQuestionV1Request = Parameters<ReturnType<typeof useApiClient>["askQuestion"]>[1];
export type AskQuestionV1Result = Components.Schemas.QueryResponseV1;

export type AskQuestionResult = AskQuestionV1Result;

export const useAskQuestion = () => {
  const client = useApiClient();
  const queryClient = useQueryClient();

  const askQuestionMutation = useMutation({
    mutationFn: (params: AskQuestionV1Request) => client.askQuestion(null, params),
    onSettled() {
      queryClient.invalidateQueries({ queryKey: [ChatListQueryKey] });
      queryClient.invalidateQueries({ queryKey: MetadataQueryKey });
    },
  });

  return askQuestionMutation;
};

export const useSubmitAnswerFeedback = () => {
  const client = useApiClient();

  const submitAnswerFeedbackMutation = useMutation({
    mutationFn: (
      params: Components.Schemas.QueryResponseFeedbackRequest & Paths.SubmitFeedback.PathParameters
    ) =>
      client.submitFeedback(
        { chatId: params.chatId, messageId: params.messageId },
        {
          score: params.score,
          modelUsed: params.modelUsed,
        }
      ),
  });

  return submitAnswerFeedbackMutation;
};

const mapScoreToFeedback = (score: number) =>
  score > 0
    ? ("positive" as AnswerFeedbackScore)
    : score < 0
      ? ("negative" as AnswerFeedbackScore)
      : undefined;

const listChatMessagesQueryKey = (chatId?: string) => ["list_chat_messages", chatId];
export const useListChatMessages = (chatId?: string) => {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const history = useHistory();

  const listChatMessages = useQuery({
    queryKey: listChatMessagesQueryKey(chatId),
    queryFn: () => client.getChatMessagesV1({ chatId: chatId! }),
    enabled: !!chatId,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    select(data) {
      const messages: ChatMessage[] = data.data.flatMap<ChatMessage>((queryResponse) => [
        {
          user: "user",
          content: queryResponse.query,
        },
        {
          user: "maite",
          id: queryResponse.id,
          chatId: queryResponse.chatId,
          content: queryResponse.content,
          isError: queryResponse.isError ?? false,
          error: queryResponse.error ?? "",
          feedbackScore: isNil(queryResponse.score)
            ? undefined
            : mapScoreToFeedback(queryResponse.score),
          debug: queryResponse.debug,
          isScenariosQuery: queryResponse.detail?.isScenariosQuery ?? false,
        },
      ]);

      return messages;
    },
    retry(attempt) {
      if (attempt < 3) {
        return true;
      }

      history.push("/maite");
      return false;
    },
  });

  const insertOptimistically = useCallback(
    (result: AxiosResponse<Components.Schemas.QueryResponseV1, any>) => {
      const optimisticChatId = result.data.chatId;
      queryClient.cancelQueries({ queryKey: listChatMessagesQueryKey(optimisticChatId) });
      queryClient.setQueryData(
        listChatMessagesQueryKey(optimisticChatId),
        (data?: AxiosResponse<Paths.GetChatMessagesV1.Responses.$200, any>) => {
          if (!data) {
            return {
              data: [
                {
                  query: result.data.query,
                  id: result.data.id,
                  content: result.data.content,
                  chatId: result.data.chatId,
                  isError: result.data.isError,
                  error: result.data.error,
                  debug: result.data.debug,
                  detail: {
                    isScenariosQuery: result.data.detail?.isScenariosQuery ?? false,
                  },
                },
              ],
            };
          }

          const existingMessage = data.data.find(
            (item) => item.id === result.data.id && item.chatId === result.data.chatId
          );
          if (existingMessage) {
            return data;
          }

          return {
            data: [
              ...data.data,
              {
                query: result.data.query,
                id: result.data.id,
                content: result.data.content,
                chatId: result.data.chatId,
                isError: result.data.isError,
                error: result.data.error,
                debug: result.data.debug,
                detail: {
                  isScenariosQuery: result.data.detail?.isScenariosQuery ?? false,
                },
              },
            ],
          };
        }
      );
    },
    [queryClient, chatId]
  );

  const updateFeedbackOptimistically = useCallback(
    (result: AxiosResponse<Components.Schemas.QueryResponseFeedbackResult, any>) => {
      const optimisticChatId = result.data.chatId;
      queryClient.cancelQueries({ queryKey: listChatMessagesQueryKey(optimisticChatId) });
      queryClient.setQueryData(
        listChatMessagesQueryKey(optimisticChatId),
        (data?: AxiosResponse<Paths.GetChatMessagesV1.Responses.$200, any>) => {
          if (!data) {
            return;
          }

          return {
            data: data.data.map((item) =>
              item.id === result.data.messageId && item.chatId === result.data.chatId
                ? {
                    ...item,
                    score: result.data.score,
                  }
                : item
            ),
          };
        }
      );
    },
    [queryClient]
  );

  return {
    ...listChatMessages,
    insertOptimistically,
    updateFeedbackOptimistically,
  };
};

const ChatListQueryKey = "list_chats";
export const useListChats = () => {
  const client = useApiClient();

  const chatListQuery = useQuery({
    queryKey: [ChatListQueryKey],
    queryFn: () => client.getChatsV1(),
    placeholderData: keepPreviousData,
  });

  return chatListQuery;
};

export const useListDocuments = () => {
  const client = useApiClient();
  return useQuery({
    queryKey: ["listDocuments"],
    queryFn: () => client.listDocuments(),
    select(data) {
      return data.data;
    },
  });
};

export const useUploadDocument = () => {
  const client = useApiClient();

  const mutationFn = ({ name, file }: { name: string; file: File }) => {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("file", file);

    const path: keyof PathsDictionary = "/api/v1/documents/upload";

    type Result = {
      id: string;
      message: string;
    };

    return client.post<Result>(path, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  return useMutation({
    mutationFn,
  });
};

export const useArchiveDocument = () => {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => client.archiveDocument(documentId),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: ["listDocuments"] });
    },
  });
};

export const useCreateChat = () => {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => client.createChat(),
    onSuccess() {
      // Invalidate chat list to refresh the list with the new chat
      queryClient.invalidateQueries({ queryKey: [ChatListQueryKey] });
    },
  });
};
